import React, { useEffect, useState, useMemo } from 'react';
import classnames from 'classnames';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useMutation } from 'react-query';
import { cloneDeep } from 'lodash';
import restFactory from 'v2/utils/restFactory';
import { FileSelectionFormFields } from '../FileSelection/FileSelection';
import styles from './headersMapping.module.scss';
import {
  GeneralSelect,
  OptionType,
} from 'v2/form/CustomSelectors/GeneralSelect';
import {
  GroupedEntityOptions,
  drmPageEntityMapping,
  useEntityOptions,
} from 'v2/common/BulkImport/BulkImportModal/models';
import InProgressUploadMessage from '../../SuccessfulUploadMessage/InProgressUploadMessage';
import { isDRMEntityBulkImport } from '../../utils';

type Option = OptionType & { options?: Option[] };

const getFilteredOptions = (options: Option[], selectedOptions: Option[]) => {
  if (selectedOptions.length === 0) {
    return options;
  }

  const filteredOptions = options.filter((option) =>
    selectedOptions.every(
      (selectedOption) =>
        option.value !== selectedOption.value &&
        option.label !== selectedOption.label
    )
  );

  for (const option of filteredOptions) {
    if (option.options) {
      option.options = getFilteredOptions(option.options, selectedOptions);
    }
  }

  return filteredOptions;
};

interface HeadersMappingProps {
  isStepActive: boolean;
  isBulkUploadFileLoading: boolean;
}

const HeadersMapping = ({
  isStepActive,
  isBulkUploadFileLoading,
}: HeadersMappingProps): JSX.Element => {
  const entityOptions = useEntityOptions();
  const { control, register, setValue, getValues } =
    useFormContext<FileSelectionFormFields>();
  const { bulkImportType } = useWatch({ control });
  const [baseOptions, setBaseOptions] =
    useState<Array<GroupedEntityOptions> | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  const formValues = getValues();
  const { headers } = formValues;

  const { mutate: loadCustomFields, data: customFieldOptions } = useMutation<
    Option[]
  >(async () => {
    if (!bulkImportType) {
      return [];
    }

    try {
      const data = await restFactory.get<string[]>(
        `/dj/api/v1/${drmPageEntityMapping[bulkImportType]}/custom_fields`
      );
      return data.map((customField) => ({
        label: customField,
        value: customField,
      }));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (bulkImportType && isDRMEntityBulkImport(bulkImportType)) {
      const baseOptions = entityOptions[bulkImportType];
      setBaseOptions(baseOptions);
      loadCustomFields();
    }
  }, [bulkImportType]);

  const options = useMemo(() => {
    if (!baseOptions || baseOptions.length < 2) {
      return null;
    }

    const optionalOptions = baseOptions[1].options.concat(
      customFieldOptions || []
    );

    return [baseOptions[0], { ...baseOptions[1], options: optionalOptions }];
  }, [baseOptions, customFieldOptions]);

  useEffect(() => {
    if (formValues.mapping) {
      for (const nomadColumn of Object.keys(formValues.mapping)) {
        setValue(`mapping.${nomadColumn}`, '');
      }
    }

    if (formValues.store) {
      for (const fileColumn of Object.keys(formValues.store)) {
        setValue(`store.${fileColumn}`, {
          label: '',
          value: '',
        });
      }
    }

    setSelectedOptions([]);
  }, [bulkImportType, headers]);

  if (!headers || headers.length === 0 || !bulkImportType || !options) {
    return <></>;
  }

  const filteredOptions = getFilteredOptions(
    cloneDeep(options) as unknown as Option[],
    cloneDeep(selectedOptions)
  );

  return (
    <>
      {isBulkUploadFileLoading ? (
        <InProgressUploadMessage />
      ) : (
        <>
          <span className="text-muted">
            Please match the column titles in your document to the <br />
            corresponding fields below.
          </span>
          <table className={classnames(styles.table)}>
            <thead>
              <tr>
                <th>column header from file</th>
                <th>nomad data field</th>
              </tr>
            </thead>

            <tbody>
              {headers.map((fileHeader, index) => (
                <tr key={index}>
                  <td>{fileHeader}</td>
                  <td>
                    <Controller
                      name={`store.${fileHeader}`}
                      control={control}
                      render={({
                        field: { value: selectedOption, onChange },
                      }) => {
                        return (
                          <GeneralSelect
                            options={filteredOptions}
                            selectedOption={selectedOption}
                            placeHolder="Select"
                            isClearable
                            fullWidth
                            handleChange={(option) => {
                              onChange(option);

                              // select the new option
                              if (option?.value) {
                                setValue(`mapping.${option.value}`, fileHeader);
                                setSelectedOptions([
                                  ...selectedOptions,
                                  option,
                                ]);
                              }

                              // remove the previous selection
                              if (selectedOption?.value) {
                                setValue(`mapping.${selectedOption.value}`, '');

                                const filteredOption = getFilteredOptions(
                                  selectedOptions,
                                  [selectedOption]
                                );

                                if (option) {
                                  setSelectedOptions([
                                    ...filteredOption,
                                    option,
                                  ]);
                                } else {
                                  setSelectedOptions([...filteredOption]);
                                }
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {options[0].options.map((option, index) => (
            <input
              key={index}
              hidden
              {...register(`mapping.${option.value}`, {
                required: isStepActive,
              })}
            />
          ))}
          {options[1].options.map((option, index) => (
            <input
              key={index}
              hidden
              {...register(`mapping.${option.value}`)}
            />
          ))}
        </>
      )}
    </>
  );
};

export default HeadersMapping;
